import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { FormField } from '../../../shared/ui/FormField';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CaptureFieldsEditor, CaptureField } from '../../../shared/ui/CaptureFieldsEditor';
import { SettingsIcon, BuildingIcon, PaletteIcon, CreditCardIcon, UsersIcon, ScrollTextIcon, BellIcon, ShieldIcon, FormInputIcon, PercentIcon, ShoppingBagIcon, PackageIcon, InfoIcon, TagIcon, DollarSignIcon, ClockIcon, UploadIcon, PlusIcon, TrashIcon, MailIcon, CheckCircleIcon, WalletIcon, LockIcon, AlertCircleIcon, FileTextIcon, MoreHorizontalIcon, ActivityIcon, SearchIcon, DownloadIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, ClipboardListIcon } from 'lucide-react';
type SettingsTab = 'company' | 'branding' | 'users' | 'payout' | 'general' | 'payment-providers' | 'receipt' | 'fields' | 'notifications' | 'security' | 'subscription-plan' | 'shop-settings' | 'order-settings';
interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: any;
  category: 'account' | 'payment' | 'ecommerce' | 'system';
}
interface ActivityLog {
  id: string;
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  action: string;
  section: string;
  description: string;
  objectId: string;
  details: {
    amount?: string;
  };
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'warning' | 'error' | 'info';
}
export function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const currency = getTenant().currency.code;
  // ---- State ----
  const [companyInfo, setCompanyInfo] = useState({
    merchantId: 'MBNK-MY-001234',
    businessName: 'Bila Bila Mart',
    registrationNumber: '202301234567',
    gstNumber: 'MY123456789',
    businessEmail: 'billing@bilabilamart.my',
    businessPhone: '+60 3-1234 5678',
    language: 'en-US'
  });
  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: getTenant().brand.logo?.light || '',
    primaryColor: '#FFC70A',
    accentColor: '#2563eb'
  });
  const [generalSettings, setGeneralSettings] = useState({
    defaultExpiryDays: 7,
    redirectUrl: 'https://bilabilamart.my/payment-success',
    enableAutoReminders: true
  });
  const [receiptSettings, setReceiptSettings] = useState({
    showCompanyAddress: true,
    companyAddress: 'Lot 123, Jalan Sultan Ismail\nKuala Lumpur 50250\nMalaysia',
    ccEmail: 'receipts@bilabilamart.my',
    footerText: 'Thank you for your business!'
  });
  const [paymentMethods, setPaymentMethods] = useState([{
    id: 'visa',
    name: 'Visa',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'amex',
    name: 'American Express',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'fpx',
    name: 'FPX (Online Banking)',
    enabled: true,
    icon: BuildingIcon,
    category: 'banking'
  }, {
    id: 'tng',
    name: "Touch 'n Go eWallet",
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'grabpay',
    name: 'GrabPay',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'duitnow',
    name: 'DuitNow QR',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'boost',
    name: 'Boost',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'shopeepay',
    name: 'ShopeePay',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }]);
  const [users, setUsers] = useState([{
    id: '1',
    name: 'Simon Lee',
    email: 'simon@bilabilamart.my',
    role: 'Owner',
    status: 'Active'
  }, {
    id: '2',
    name: 'Sarah Tan',
    email: 'sarah@bilabilamart.my',
    role: 'Finance',
    status: 'Active'
  }, {
    id: '3',
    name: 'Ahmad Rahman',
    email: 'ahmad@bilabilamart.my',
    role: 'Operations',
    status: 'Active'
  }]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showAllInvitePerms, setShowAllInvitePerms] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Operations');
  const [showRoleManagementModal, setShowRoleManagementModal] = useState(false);
  const [openUserMenuId, setOpenUserMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    email: string;
    role: string;
  } | null>(null);
  // Role definitions with permissions
  const rolePermissions = {
    Owner: {
      description: 'Full access to all features and settings',
      permissions: {
        Dashboard: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Payments: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Users: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Reports: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        }
      }
    },
    'Merchant Management': {
      description: 'Create/edit/view merchant accounts, KYC, onboarding',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    'Account Management': {
      description: 'View and manage existing merchant accounts, support actions, no financial controls',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Sales: {
      description: 'View merchant pipeline, create leads, view commercial terms, no settlement/finance access',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: false,
          insert: true,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Finance: {
      description: 'View settlements, payouts, MDR, invoices, refunds; no merchant onboarding rights',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: true
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: true,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Operations: {
      description: 'Transaction monitoring, dispute handling, reconciliation, basic merchant edits',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    }
  };
  const [termsSettings, setTermsSettings] = useState({
    termsText: 'By using our payment services, you agree to our terms and conditions.',
    privacyPolicy: 'We collect and process payment information in accordance with Malaysian data protection regulations.'
  });
  const [presetMetadataFields, setPresetMetadataFields] = useState<MetadataItem[]>([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number'
  }, {
    key: 'order_id',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Internal order ID'
  }]);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    paymentReceived: true,
    paymentFailed: true,
    refundProcessed: true,
    disputeOpened: true,
    settlementCompleted: true,
    lowBalance: true
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    ipWhitelist: '',
    apiKeyVisible: false
  });
  const [selectedShop, setSelectedShop] = useState('Beach Shop');
  const [shopSettings, setShopSettings] = useState({
    inventoryTracking: true,
    lowStockThreshold: 5,
    notifyOnLowStock: false,
    allowBackorders: false,
    hideOutOfStock: false,
    enableTaxCalculation: false,
    pricesIncludeTax: false,
    processingFee: 0,
    shippingFee: 0
  });
  const [taxRates, setTaxRates] = useState([{
    id: '1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: '2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: '3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: '1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: '3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: '4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [orderSettings, setOrderSettings] = useState({
    orderPrefix: 'ORD',
    startingNumber: 1000,
    autoInvoice: true,
    requireSignature: false,
    allowPartialPayments: true,
    defaultPaymentTerms: 'Net 30'
  });
  // Payout settings state
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [payoutDescriptor, setPayoutDescriptor] = useState('');
  const [shopFees, setShopFees] = useState([{
    id: '1',
    name: 'Processing Fee',
    type: 'Percentage',
    amount: 2.5,
    condition: 'Applied to all orders'
  }, {
    id: '2',
    name: 'Shipping Fee',
    type: 'Fixed',
    amount: 5.0,
    condition: 'Applied to orders over MYR 100'
  }]);
  const [shopPayoutSettings, setShopPayoutSettings] = useState({
    bankAccount: '1234567890',
    frequency: 'Daily',
    minimumAmount: 100.0
  });
  const [shopNotifications, setShopNotifications] = useState({
    confirmationMessage: 'Thank you for your order! We will process it shortly.',
    notificationEmail: 'notifications@bilabilamart.my',
    notificationPhone: '+60 3-1234 5678',
    sendOrderNotifications: true,
    sendLowStockAlerts: false
  });
  // Activity Logs state
  const [activityLogs] = useState(() => {
    const logs = [];
    const now = new Date();
    const actions = [{
      type: 'created',
      section: 'Payments',
      desc: 'Payment created'
    }, {
      type: 'updated',
      section: 'Payments',
      desc: 'Payment updated'
    }, {
      type: 'deleted',
      section: 'Payments',
      desc: 'Payment deleted'
    }, {
      type: 'created',
      section: 'Customers',
      desc: 'Customer created'
    }, {
      type: 'updated',
      section: 'Customers',
      desc: 'Customer updated'
    }, {
      type: 'deleted',
      section: 'Customers',
      desc: 'Customer deleted'
    }, {
      type: 'created',
      section: 'Invoices',
      desc: 'Invoice created'
    }, {
      type: 'updated',
      section: 'Invoices',
      desc: 'Invoice updated'
    }, {
      type: 'approved',
      section: 'Bills',
      desc: 'Bill approved'
    }, {
      type: 'created',
      section: 'Refunds',
      desc: 'Refund created'
    }, {
      type: 'updated',
      section: 'Settings',
      desc: 'Settings updated'
    }, {
      type: 'created',
      section: 'Users',
      desc: 'User invited'
    }, {
      type: 'updated',
      section: 'Security',
      desc: 'Security settings updated'
    }];
    const realUsers = [{
      id: '1',
      name: 'Simon Lee',
      email: 'simon@bilabilamart.my',
      role: 'Owner'
    }, {
      id: '2',
      name: 'Sarah Tan',
      email: 'sarah@bilabilamart.my',
      role: 'Finance'
    }, {
      id: '3',
      name: 'Ahmad Rahman',
      email: 'ahmad@bilabilamart.my',
      role: 'Operations'
    }];
    for (let i = 0; i < 80; i++) {
      const action = actions[Math.floor(Math.random() * actions.length)];
      const user = realUsers[Math.floor(Math.random() * realUsers.length)];
      const minutesAgo = Math.floor(Math.random() * 43200); // 30 days in minutes
      const timestamp = new Date(now.getTime() - minutesAgo * 60000);
      logs.push({
        id: `log-${i + 1}`,
        timestamp: timestamp.toISOString(),
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        },
        action: action.type,
        section: action.section,
        description: action.desc,
        objectId: `${action.section.toLowerCase()}_${Math.random().toString(36).substr(2, 9)}`,
        details: {
          amount: Math.random() > 0.5 ? `MYR ${(Math.random() * 1000).toFixed(2)}` : undefined
        },
        ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.3.'
      });
    }
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });
  const [logFilters, setLogFilters] = useState({
    search: '',
    dateRange: '7days',
    user: 'all',
    actionType: 'all',
    section: 'all'
  });
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  // Merchant Requests state
  const [merchantRequests] = useState(() => {
    const now = new Date();
    return [{
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Sarah Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ahmad Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Simon Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Sarah Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ahmad Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const tabs: TabConfig[] = [
  // ACCOUNT
  {
    id: 'company',
    label: 'Company',
    icon: BuildingIcon,
    category: 'account'
  }, {
    id: 'branding',
    label: 'Branding',
    icon: PaletteIcon,
    category: 'account'
  }, {
    id: 'users',
    label: 'Manage Users',
    icon: UsersIcon,
    category: 'account'
  }, {
    id: 'payout',
    label: 'Payout',
    icon: DollarSignIcon,
    category: 'account'
  },
  // PAYMENT SETTINGS
  {
    id: 'general',
    label: 'General',
    icon: SettingsIcon,
    category: 'payment'
  }, {
    id: 'payment-providers',
    label: 'Payment Providers',
    icon: CreditCardIcon,
    category: 'payment'
  }, {
    id: 'receipt',
    label: 'Receipt Information',
    icon: MailIcon,
    category: 'payment'
  }, {
    id: 'fields',
    label: 'Information Fields',
    icon: FormInputIcon,
    category: 'payment'
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: BellIcon,
    category: 'payment'
  }, {
    id: 'security',
    label: 'Security',
    icon: ShieldIcon,
    category: 'payment'
  },
  // E-COMMERCE
  {
    id: 'subscription-plan',
    label: 'Subscription Plan',
    icon: PercentIcon,
    category: 'ecommerce'
  }, {
    id: 'shop-settings',
    label: 'Shop Settings',
    icon: ShoppingBagIcon,
    category: 'ecommerce'
  }, {
    id: 'order-settings',
    label: 'Order Settings',
    icon: PackageIcon,
    category: 'ecommerce'
  }];
  const categories = [{
    id: 'account',
    label: 'ACCOUNT'
  }, {
    id: 'payment',
    label: 'PAYMENT SETTINGS'
  }, {
    id: 'ecommerce',
    label: 'E-COMMERCE'
  }];
  const SaveButton = () => <div className="pt-4" data-id="element-9139">
      <button className="px-4 py-2 bg-[#FFC70A] text-gray-900 font-medium rounded-md hover:bg-[#E6B309] transition-colors" data-id="element-9140">
        Save Changes
      </button>
    </div>;
  // ---- Render functions ----
  const renderCompanySettings = () => <div className="space-y-6" data-id="element-9141">
      <div data-id="element-9142">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9143">
          Company Information
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9144">
          Manage your business details and merchant account information
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9145">
        <div className="flex items-start gap-3" data-id="element-9146">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9147" />
          <div className="text-sm text-gray-700" data-id="element-9148">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9149">
              Verified Merchant Account
            </p>
            <p className="text-xs" data-id="element-9150">
              Your merchant account has been verified and is active. Contact
              support if you need to update your business registration details.
            </p>
          </div>
        </div>
      </div>

      {/* Company Details Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9151">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9152">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9153">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-9154" />
            Business Details
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9155">
          <FormField label="Merchant ID" tooltip="Your unique merchant identifier" data-id="element-9156">
            <div className="flex items-center gap-2" data-id="element-9157">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50" value={companyInfo.merchantId} readOnly data-id="element-9158" />
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-green-100 text-green-800 whitespace-nowrap" data-id="element-9159">
                <CheckCircleIcon size={14} className="mr-1" data-id="element-9160" />
                VERIFIED
              </span>
            </div>
          </FormField>

          <FormField label="Business Name" required data-id="element-9161">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={companyInfo.businessName} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessName: e.target.value
          })} data-id="element-9162" />
          </FormField>

          <FormField label="Registration Number" required data-id="element-9163">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={companyInfo.registrationNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            registrationNumber: e.target.value
          })} data-id="element-9164" />
          </FormField>

          <FormField label="GST/SST Number" data-id="element-9165">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={companyInfo.gstNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            gstNumber: e.target.value
          })} data-id="element-9166" />
          </FormField>

          <FormField label="Business Email" required data-id="element-9167">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={companyInfo.businessEmail} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessEmail: e.target.value
          })} data-id="element-9168" />
          </FormField>

          <FormField label="Business Phone" required data-id="element-9169">
            <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={companyInfo.businessPhone} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessPhone: e.target.value
          })} data-id="element-9170" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9171" />
    </div>;
  const renderBrandingSettings = () => <div className="space-y-6" data-id="element-9172">
      <div data-id="element-9173">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9174">Branding</h2>
        <p className="text-sm text-gray-600" data-id="element-9175">
          Customize your brand appearance across payment pages and receipts
        </p>
      </div>

      {/* Logo Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9176">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9177">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9178">
            <PaletteIcon size={18} className="text-gray-600" data-id="element-9179" />
            Logo & Visual Identity
          </h3>
        </div>
        <div className="p-6 space-y-6" data-id="element-9180">
          <FormField label="Company Logo" description="Upload your company logo (recommended: 200x60px, PNG or SVG)." data-id="element-9181">
            <div className="flex items-center gap-4" data-id="element-9182">
              {brandingSettings.logoUrl && <div className="border border-gray-200 rounded-lg p-4 bg-white" data-id="element-9183">
                  <img src={brandingSettings.logoUrl} alt="Company Logo" className="h-12 object-contain" data-id="element-9184" />
                </div>}
              <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center transition-colors" data-id="element-9185">
                <UploadIcon size={16} className="mr-2" data-id="element-9186" />
                Upload New Logo
              </button>
            </div>
          </FormField>

          <FormField label="Primary Brand Color" description="This color will be used for buttons and accents" data-id="element-9187">
            <div className="flex items-center gap-3" data-id="element-9188">
              <input type="color" className="h-10 w-20 border border-gray-300 rounded cursor-pointer" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-9189" />
              <input type="text" className="block w-32 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-9190" />
              <div className="flex-1 text-sm text-gray-600" data-id="element-9191">
                Current: Maybank Yellow
              </div>
            </div>
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9192" />
    </div>;
  const renderGeneralSettings = () => <div className="space-y-6" data-id="element-9193">
      <div data-id="element-9194">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9195">
          General Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9196">
          Configure default behaviors and preferences for your payment system
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9197">
        <div className="flex items-start gap-3" data-id="element-9198">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9199" />
          <div className="text-sm text-gray-700" data-id="element-9200">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9201">
              Payment Link Defaults
            </p>
            <p className="text-xs" data-id="element-9202">
              These settings apply to all new payment links and invoices. You
              can override them individually when creating each payment.
            </p>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9203">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9204">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9205">
            <SettingsIcon size={18} className="text-gray-600" data-id="element-9206" />
            Default Configuration
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9207">
          <FormField label="Default Expiry Days" description="Number of days before payment links expire" data-id="element-9208">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={generalSettings.defaultExpiryDays} onChange={e => setGeneralSettings({
            ...generalSettings,
            defaultExpiryDays: parseInt(e.target.value)
          })} data-id="element-9209" />
          </FormField>

          <FormField label="Redirect URL" description="Where customers are sent after successful payment" data-id="element-9210">
            <input type="url" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent font-mono text-sm" value={generalSettings.redirectUrl} onChange={e => setGeneralSettings({
            ...generalSettings,
            redirectUrl: e.target.value
          })} data-id="element-9211" />
          </FormField>

          <div className="pt-2" data-id="element-9212">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-9213">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#FFC70A] focus:ring-[#FFC70A]" checked={generalSettings.enableAutoReminders} onChange={e => setGeneralSettings({
              ...generalSettings,
              enableAutoReminders: e.target.checked
            })} data-id="element-9214" />
              <div data-id="element-9215">
                <p className="text-sm font-medium text-gray-900" data-id="element-9216">
                  Enable automatic payment reminders
                </p>
                <p className="text-xs text-gray-500" data-id="element-9217">
                  Send reminder emails for unpaid invoices
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-9218" />
    </div>;
  const renderReceiptSettings = () => <div className="space-y-6" data-id="element-9219">
      <div data-id="element-9220">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9221">
          Receipt Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9222">
          Customize the receipts sent to your customers after payment
        </p>
      </div>

      {/* Receipt Customization Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9223">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9224">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9225">
            <MailIcon size={18} className="text-gray-600" data-id="element-9226" />
            Receipt Customization
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9227">
          <div className="pb-2" data-id="element-9228">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-9229">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#FFC70A] focus:ring-[#FFC70A]" checked={receiptSettings.showCompanyAddress} onChange={e => setReceiptSettings({
              ...receiptSettings,
              showCompanyAddress: e.target.checked
            })} data-id="element-9230" />
              <div data-id="element-9231">
                <p className="text-sm font-medium text-gray-900" data-id="element-9232">
                  Show company address on receipts
                </p>
                <p className="text-xs text-gray-500" data-id="element-9233">
                  Display your business address on customer receipts
                </p>
              </div>
            </label>
          </div>

          <FormField label="Company Address" description="This address will appear on receipts if enabled above" data-id="element-9234">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" rows={3} value={receiptSettings.companyAddress} onChange={e => setReceiptSettings({
            ...receiptSettings,
            companyAddress: e.target.value
          })} data-id="element-9235" />
          </FormField>

          <FormField label="CC Email" description="Automatically CC this email on all receipts" data-id="element-9236">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={receiptSettings.ccEmail} onChange={e => setReceiptSettings({
            ...receiptSettings,
            ccEmail: e.target.value
          })} data-id="element-9237" />
          </FormField>

          <FormField label="Footer Text" description="Custom message at the bottom of receipts" data-id="element-9238">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={receiptSettings.footerText} onChange={e => setReceiptSettings({
            ...receiptSettings,
            footerText: e.target.value
          })} data-id="element-9239" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9240" />
    </div>;
  const renderPaymentProviders = () => <div className="space-y-6" data-id="element-9241">
      <div data-id="element-9242">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9243">
          Payment Providers
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9244">
          Enable or disable payment methods available to your customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-9245">
        <div className="flex items-start gap-3" data-id="element-9246">
          <AlertCircleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9247" />
          <div className="text-sm text-gray-700" data-id="element-9248">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9249">
              Payment Method Availability
            </p>
            <p className="text-xs" data-id="element-9250">
              Disabling a payment method will hide it from all payment pages.
              Customers won't be able to use disabled methods for new payments.
            </p>
          </div>
        </div>
      </div>

      {/* Card Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9251">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9252">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9253">
            <CreditCardIcon size={18} className="text-gray-600" data-id="element-9254" />
            Card Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9255">
          {paymentMethods.filter(m => m.category === 'card').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9256">
                  <div className="flex items-center gap-3" data-id="element-9257">
                    <Icon size={20} className="text-gray-600" data-id="element-9258" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9259">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9260">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9261" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9262"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* E-Wallet Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9263">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9264">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9265">
            <WalletIcon size={18} className="text-gray-600" data-id="element-9266" />
            E-Wallets & QR Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9267">
          {paymentMethods.filter(m => m.category === 'ewallet').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9268">
                  <div className="flex items-center gap-3" data-id="element-9269">
                    <Icon size={20} className="text-gray-600" data-id="element-9270" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9271">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9272">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9273" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9274"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* Online Banking */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9275">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9276">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9277">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-9278" />
            Online Banking
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9279">
          {paymentMethods.filter(m => m.category === 'banking').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9280">
                  <div className="flex items-center gap-3" data-id="element-9281">
                    <Icon size={20} className="text-gray-600" data-id="element-9282" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9283">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9284">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9285" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9286"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>
    </div>;
  const renderUsers = () => <div className="space-y-6" data-id="element-9287">
      <div className="flex items-center justify-between" data-id="element-9288">
        <div data-id="element-9289">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9290">
            Users & Permissions
          </h2>
          <p className="text-sm text-gray-600" data-id="element-9291">
            Manage team members and their access levels
          </p>
        </div>
        <div className="flex gap-3" data-id="element-9292">
          <button onClick={() => setShowRoleManagementModal(true)} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md flex items-center hover:bg-gray-50 transition-colors" data-id="element-9293">
            <ShieldIcon size={16} className="mr-2" data-id="element-9294" />
            Permissions
          </button>
          <button onClick={() => setShowInviteForm(!showInviteForm)} className="px-4 py-2 bg-[#FFC70A] text-gray-900 font-medium rounded-md flex items-center hover:bg-[#E6B309] transition-colors" data-id="element-9295">
            <PlusIcon size={16} className="mr-2" data-id="element-9296" />
            Invite User
          </button>
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9297">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9298">
            <h3 className="text-md font-semibold text-gray-900" data-id="element-9299">
              Invite New User
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-9300">
            <FormField label="Email Address" required data-id="element-9301">
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com" data-id="element-9302" />
            </FormField>
            <FormField label="Role" required data-id="element-9303">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-9304">
                <option value="Owner" data-id="element-9305">Owner</option>
                <option value="Merchant Management" data-id="element-9306">Merchant Management</option>
                <option value="Account Management" data-id="element-9307">Account Management</option>
                <option value="Sales" data-id="element-9308">Sales</option>
                <option value="Finance" data-id="element-9309">Finance</option>
                <option value="Operations" data-id="element-9310">Operations</option>
              </select>
              {inviteRole && rolePermissions[inviteRole as keyof typeof rolePermissions] && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-9311">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-9312">
                      {inviteRole}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-9313">
                      {rolePermissions[inviteRole as keyof typeof rolePermissions].description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-9314">
                      {Object.entries(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).slice(0, showAllInvitePerms ? Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length : 5).map(([section, perms]) => {
                const activePerms = Object.entries(perms).filter(([_, val]) => val).map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
                if (activePerms.length === 0) return null;
                return <span key={section} className="text-xs px-2 py-1 bg-white border border-blue-200 rounded text-gray-700" data-id="element-9315">
                              {section}: {activePerms.join(', ')}
                            </span>;
              })}
                      {Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length > 5 && <button type="button" onClick={() => setShowAllInvitePerms(!showAllInvitePerms)} className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" data-id="element-9316">
                          {showAllInvitePerms ? 'Show less' : `+${Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length - 5} more`}
                        </button>}
                    </div>
                  </div>}
            </FormField>
            <div className="flex gap-3 pt-2" data-id="element-9317">
              <button onClick={() => {
            if (inviteEmail && inviteRole) {
              const newUser = {
                id: `user-${Date.now()}`,
                name: inviteEmail.split('@')[0],
                email: inviteEmail,
                role: inviteRole,
                status: 'Invited' as const
              };
              setUsers([...users, newUser]);
              setInviteEmail('');
              setInviteRole('Manager');
              setShowInviteForm(false);
            }
          }} className="px-4 py-2 bg-[#FFC70A] text-gray-900 font-medium rounded-md hover:bg-[#E6B309] transition-colors" data-id="element-9318">
                Send Invite
              </button>
              <button onClick={() => {
            setShowInviteForm(false);
            setInviteEmail('');
            setInviteRole('Operations');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-9319">
                Cancel
              </button>
            </div>
          </div>
        </div>}

      {/* Users Table */}
      <div className="border border-gray-200 rounded-lg" data-id="element-9320">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg" data-id="element-9321">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9322">
            <UsersIcon size={18} className="text-gray-600" data-id="element-9323" />
            Team Members ({users.length})
          </h3>
        </div>
        <div data-id="element-9324">
          <table className="w-full" data-id="element-9325">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9326">
              <tr data-id="element-9327">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9328">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9329">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9330">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9331">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9332">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9333">
              {users.map(u => <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-id="element-9334">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-9335">
                    {u.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-9336">{u.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-9337">{u.role}</td>
                  <td className="px-6 py-4" data-id="element-9338">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.status === 'Active' ? 'bg-green-100 text-green-800' : u.status === 'Invited' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9339">
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-9340">
                    <div className="flex items-center justify-end gap-2" data-id="element-9341">
                      {u.role === 'Owner' ? <span className="text-gray-400 text-xs" data-id="element-9342">Owner</span> : <div className="relative inline-block" data-id="element-9343">
                          <button type="button" aria-label={`Actions for ${u.name}`} aria-haspopup="menu" aria-expanded={openUserMenuId === u.id} onClick={() => setOpenUserMenuId(openUserMenuId === u.id ? null : u.id)} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-9344">
                            <MoreHorizontalIcon size={18} data-id="element-9345" />
                          </button>
                          {openUserMenuId === u.id && <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenUserMenuId(null)} data-id="element-9346" />
                              <div role="menu" className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20" data-id="element-9347">
                                {u.status !== 'Invited' && <button role="menuitem" onClick={() => {
                          setEditingUser({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            role: u.role
                          });
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9348">
                                    Edit
                                  </button>}
                                {u.status === 'Active' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Disabled' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50" data-id="element-9349">
                                    Disable
                                  </button>}
                                {u.status === 'Disabled' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Active' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50" data-id="element-9350">
                                    Enable
                                  </button>}
                                <button role="menuitem" onClick={() => {
                          if (confirm(u.status === 'Invited' ? `Cancel invite for ${u.email}?` : `Are you sure you want to delete ${u.name}?`)) {
                            setUsers(users.filter(user => user.id !== u.id));
                          }
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-9351">
                                  {u.status === 'Invited' ? 'Cancel Invite' : 'Delete'}
                                </button>
                              </div>
                            </>}
                        </div>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-9352">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-id="element-9353">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-9354">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-9355">
                Edit Team Member
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-9356">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9357">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-9358" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-9359">
              <FormField label="Name" data-id="element-9360">
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={editingUser.name} onChange={e => setEditingUser({
              ...editingUser,
              name: e.target.value
            })} data-id="element-9361" />
              </FormField>
              <FormField label="Email" data-id="element-9362">
                <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={editingUser.email} onChange={e => setEditingUser({
              ...editingUser,
              email: e.target.value
            })} data-id="element-9363" />
              </FormField>
              <FormField label="Role" data-id="element-9364">
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={editingUser.role} onChange={e => setEditingUser({
              ...editingUser,
              role: e.target.value
            })} data-id="element-9365">
                  <option value="Merchant Management" data-id="element-9366">
                    Merchant Management
                  </option>
                  <option value="Account Management" data-id="element-9367">Account Management</option>
                  <option value="Sales" data-id="element-9368">Sales</option>
                  <option value="Finance" data-id="element-9369">Finance</option>
                  <option value="Operations" data-id="element-9370">Operations</option>
                </select>
              </FormField>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50" data-id="element-9371">
              <button onClick={() => setEditingUser(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-9372">
                Cancel
              </button>
              <button onClick={() => {
            setUsers(users.map(user => user.id === editingUser.id ? {
              ...user,
              name: editingUser.name,
              email: editingUser.email,
              role: editingUser.role
            } : user));
            setEditingUser(null);
          }} className="px-4 py-2 text-sm font-medium bg-[#FFC70A] text-gray-900 rounded-md hover:bg-[#E6B309]" data-id="element-9373">
                Save Changes
              </button>
            </div>
          </div>
        </div>}

      {/* Role Management Modal */}
      {showRoleManagementModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-9374">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-9375">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-9376">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-9377">
                Role Permissions Matrix
              </h3>
              <button onClick={() => setShowRoleManagementModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-9378">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9379">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-9380" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1" data-id="element-9381">
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-9382">
                {Object.entries(rolePermissions).map(([role, config]) => <div key={role} className="border border-gray-200 rounded-lg p-4" data-id="element-9383">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-9384">{role}</h4>
                    <p className="text-xs text-gray-600" data-id="element-9385">
                      {config.description}
                    </p>
                  </div>)}
              </div>
              <div className="overflow-x-auto" data-id="element-9386">
                <table className="w-full border border-gray-200 rounded-lg" data-id="element-9387">
                  <thead className="bg-gray-50" data-id="element-9388">
                    <tr data-id="element-9389">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-9390">
                        Section
                      </th>
                      {Object.keys(rolePermissions).map(role => <th key={role} colSpan={5} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-l border-gray-200" data-id="element-9391">
                          {role}
                        </th>)}
                    </tr>
                    <tr data-id="element-9392">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9393"></th>
                      {Object.keys(rolePermissions).map(role => <Fragment key={role} data-id="element-9394">
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-l border-gray-200" data-id="element-9395">
                            R
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9396">
                            W
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9397">
                            M
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9398">
                            I
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9399">
                            A
                          </th>
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-9400">
                    {Object.keys(rolePermissions.Owner.permissions).map(section => <tr key={section} className="hover:bg-gray-50" data-id="element-9401">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9402">
                            {section}
                          </td>
                          {Object.entries(rolePermissions).map(([role, config]) => {
                    const perms = config.permissions[section as keyof typeof config.permissions];
                    return <Fragment key={role} data-id="element-9403">
                                  <td className="px-2 py-3 text-center border-l border-gray-200" data-id="element-9404">
                                    {perms.read ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9405" /> : <span className="text-gray-300" data-id="element-9406">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9407">
                                    {perms.write ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9408" /> : <span className="text-gray-300" data-id="element-9409">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9410">
                                    {perms.modify ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9411" /> : <span className="text-gray-300" data-id="element-9412">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9413">
                                    {perms.insert ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9414" /> : <span className="text-gray-300" data-id="element-9415">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9416">
                                    {perms.approve ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9417" /> : <span className="text-gray-300" data-id="element-9418">—</span>}
                                  </td>
                                </Fragment>;
                  })}
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-gray-500" data-id="element-9419">
                <p className="font-medium mb-1" data-id="element-9420">Permission Legend:</p>
                <div className="flex flex-wrap gap-4" data-id="element-9421">
                  <span data-id="element-9422">
                    <strong data-id="element-9423">R</strong> = Read
                  </span>
                  <span data-id="element-9424">
                    <strong data-id="element-9425">W</strong> = Write
                  </span>
                  <span data-id="element-9426">
                    <strong data-id="element-9427">M</strong> = Modify
                  </span>
                  <span data-id="element-9428">
                    <strong data-id="element-9429">I</strong> = Insert
                  </span>
                  <span data-id="element-9430">
                    <strong data-id="element-9431">A</strong> = Approve
                  </span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-9432">
              <button onClick={() => setShowRoleManagementModal(false)} className="px-4 py-2 bg-[#FFC70A] text-gray-900 font-medium rounded-md hover:bg-[#E6B309] transition-colors" data-id="element-9433">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
  const renderTerms = () => <div className="space-y-6" data-id="element-9434">
      <div data-id="element-9435">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9436">
          Terms & Privacy
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9437">
          Configure legal terms and privacy policy shown to customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9438">
        <div className="flex items-start gap-3" data-id="element-9439">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9440" />
          <div className="text-sm text-gray-700" data-id="element-9441">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9442">Legal Compliance</p>
            <p className="text-xs" data-id="element-9443">
              These terms are displayed to customers during checkout. Ensure
              they comply with Malaysian data protection and consumer laws.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9444">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9445">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9446">
            <FileTextIcon size={18} className="text-gray-600" data-id="element-9447" />
            Terms & Conditions
          </h3>
        </div>
        <div className="p-6" data-id="element-9448">
          <FormField label="Terms Text" description="Displayed on payment pages and checkout" data-id="element-9449">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent font-sans" rows={6} value={termsSettings.termsText} onChange={e => setTermsSettings({
            ...termsSettings,
            termsText: e.target.value
          })} data-id="element-9450" />
          </FormField>
        </div>
      </div>

      {/* Privacy Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9451">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9452">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9453">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-9454" />
            Privacy Policy
          </h3>
        </div>
        <div className="p-6" data-id="element-9455">
          <FormField label="Privacy Policy Text" description="Information about how customer data is handled" data-id="element-9456">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent font-sans" rows={6} value={termsSettings.privacyPolicy} onChange={e => setTermsSettings({
            ...termsSettings,
            privacyPolicy: e.target.value
          })} data-id="element-9457" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9458" />
    </div>;
  const renderFields = () => <div className="space-y-6" data-id="element-9459">
      <div data-id="element-9460">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9461">
          Information Fields
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9462">
          Configure metadata fields collected at checkout
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9463">
        <div className="flex items-start gap-3" data-id="element-9464">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9465" />
          <div className="text-sm text-gray-700" data-id="element-9466">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9467">
              Custom Checkout Fields
            </p>
            <p className="text-xs" data-id="element-9468">
              Add custom fields to collect additional information from customers
              during checkout. This data will be attached to payment records and
              can be used for reporting or integration purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Fields Editor Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9469">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9470">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9471">
            <FormInputIcon size={18} className="text-gray-600" data-id="element-9472" />
            Metadata Fields
          </h3>
        </div>
        <div className="p-6" data-id="element-9473">
          <MetadataEditor metadata={presetMetadataFields} onChange={setPresetMetadataFields} data-id="element-9474" />
        </div>
      </div>
    </div>;
  const renderNotifications = () => <div className="space-y-6" data-id="element-9475">
      <div data-id="element-9476">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9477">
          Notifications
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9478">
          Manage how you receive alerts about payments and account activity
        </p>
      </div>

      {/* Notification Channels Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9479">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9480">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9481">
            <BellIcon size={18} className="text-gray-600" data-id="element-9482" />
            Notification Channels
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9483">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9484">
            <div data-id="element-9485">
              <p className="text-sm font-medium text-gray-900" data-id="element-9486">
                Email Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9487">
                Receive alerts via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9488">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.emailNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              emailNotifications: e.target.checked
            })} data-id="element-9489" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9490"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9491">
            <div data-id="element-9492">
              <p className="text-sm font-medium text-gray-900" data-id="element-9493">
                SMS Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9494">
                Receive alerts via text message
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9495">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.smsNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              smsNotifications: e.target.checked
            })} data-id="element-9496" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9497"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Event Notifications Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9498">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9499">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9500">
            <AlertCircleIcon size={18} className="text-gray-600" data-id="element-9501" />
            Event Notifications
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9502">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9503">
            <div data-id="element-9504">
              <p className="text-sm font-medium text-gray-900" data-id="element-9505">
                Payment Received
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9506">
                When a customer completes payment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9507">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentReceived} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentReceived: e.target.checked
            })} data-id="element-9508" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9509"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9510">
            <div data-id="element-9511">
              <p className="text-sm font-medium text-gray-900" data-id="element-9512">
                Payment Failed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9513">
                When a payment attempt fails
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9514">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentFailed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentFailed: e.target.checked
            })} data-id="element-9515" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9516"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9517">
            <div data-id="element-9518">
              <p className="text-sm font-medium text-gray-900" data-id="element-9519">
                Refund Processed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9520">
                When a refund is completed
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9521">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.refundProcessed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              refundProcessed: e.target.checked
            })} data-id="element-9522" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9523"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9524">
            <div data-id="element-9525">
              <p className="text-sm font-medium text-gray-900" data-id="element-9526">
                Dispute Opened
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9527">
                When a customer files a dispute
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9528">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.disputeOpened} onChange={e => setNotificationSettings({
              ...notificationSettings,
              disputeOpened: e.target.checked
            })} data-id="element-9529" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9530"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9531">
            <div data-id="element-9532">
              <p className="text-sm font-medium text-gray-900" data-id="element-9533">
                Settlement Completed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9534">
                When funds are transferred to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9535">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.settlementCompleted} onChange={e => setNotificationSettings({
              ...notificationSettings,
              settlementCompleted: e.target.checked
            })} data-id="element-9536" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9537"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9538">
            <div data-id="element-9539">
              <p className="text-sm font-medium text-gray-900" data-id="element-9540">Low Balance</p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9541">
                When your account balance is low
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9542">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.lowBalance} onChange={e => setNotificationSettings({
              ...notificationSettings,
              lowBalance: e.target.checked
            })} data-id="element-9543" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9544"></div>
            </label>
          </div>
        </div>
      </div>
    </div>;
  const renderSecurity = () => <div className="space-y-6" data-id="element-9545">
      <div data-id="element-9546">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9547">Security</h2>
        <p className="text-sm text-gray-600" data-id="element-9548">
          Manage security settings and access controls for your account
        </p>
      </div>

      {/* Security Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-9549">
        <div className="flex items-start gap-3" data-id="element-9550">
          <CheckCircleIcon size={20} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-9551" />
          <div className="text-sm text-gray-700" data-id="element-9552">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9553">
              Security Status: Protected
            </p>
            <p className="text-xs" data-id="element-9554">
              Your account is secured with two-factor authentication and session
              management. Keep these settings enabled for maximum protection.
            </p>
          </div>
        </div>
      </div>

      {/* Authentication Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9555">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9556">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9557">
            <LockIcon size={18} className="text-gray-600" data-id="element-9558" />
            Authentication & Access
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9559">
          <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-4" data-id="element-9560">
            <div data-id="element-9561">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-9562">
                Two-Factor Authentication
                {securitySettings.twoFactorEnabled && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9563">
                    Enabled
                  </span>}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-9564">
                Adds an extra layer of security to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9565">
              <input type="checkbox" className="sr-only peer" checked={securitySettings.twoFactorEnabled} onChange={e => setSecuritySettings({
              ...securitySettings,
              twoFactorEnabled: e.target.checked
            })} data-id="element-9566" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9567"></div>
            </label>
          </div>

          <FormField label="Session Timeout (minutes)" description="Automatically log out after this period of inactivity" data-id="element-9568">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
            ...securitySettings,
            sessionTimeout: parseInt(e.target.value)
          })} data-id="element-9569" />
          </FormField>
        </div>
      </div>

      {/* IP Whitelist Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9570">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9571">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9572">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-9573" />
            IP Whitelist
          </h3>
        </div>
        <div className="p-6" data-id="element-9574">
          <FormField label="Allowed IP Addresses" description="Restrict API access to specific IP addresses (one per line). Leave empty to allow all IPs." data-id="element-9575">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" rows={4} value={securitySettings.ipWhitelist} onChange={e => setSecuritySettings({
            ...securitySettings,
            ipWhitelist: e.target.value
          })} placeholder="192.168.1.1&#10;10.0.0.1" data-id="element-9576" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9577" />
    </div>;
  const renderPayoutSettings = () => {
    const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
    let pendingPayoutUpdate: {
      bank?: string;
      maskedAccount?: string;
      submittedAt?: string;
    } | null = null;
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('pendingPayoutUpdate') : null;
      if (raw) pendingPayoutUpdate = JSON.parse(raw);
    } catch {
      pendingPayoutUpdate = null;
    }
    return <div className="space-y-6" data-id="element-9578">
        <div data-id="element-9579">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9580">
            Payout Settings
          </h2>
          <p className="text-sm text-gray-600" data-id="element-9581">
            Configure bank accounts, minimum balance, and payout preferences
          </p>
        </div>

        {/* Settlement Currencies Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9582">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9583">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9584">
              <BuildingIcon size={18} className="text-gray-600" data-id="element-9585" />
              Settlement Currencies & Bank Accounts
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-9586">
              Configure bank accounts for receiving payouts in different
              currencies
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-9587">
            <table className="w-full text-left" data-id="element-9588">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9589">
                <tr data-id="element-9590">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9591">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9592">
                    Payout Bank Account
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9593">
                    <div className="flex items-center gap-1.5" data-id="element-9594">
                      Payout Fee
                      <InfoIcon size={14} className="text-gray-400" data-id="element-9595" />
                    </div>
                  </th>
                  <th className="px-6 py-3" data-id="element-9596"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9597">
                <tr className="hover:bg-gray-50 transition-colors" data-id="element-9598">
                  <td className="px-6 py-4" data-id="element-9599">
                    <div className="flex items-center gap-2" data-id="element-9600">
                      <span className="text-sm font-medium text-gray-900" data-id="element-9601">
                        MYR
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9602">
                        Default
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9603">
                    <div className="flex flex-col gap-1.5" data-id="element-9604">
                      <div className="flex items-center gap-3" data-id="element-9605">
                        <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-9606">
                          <BuildingIcon size={16} className="text-gray-500" data-id="element-9607" />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-9608">
                          <span className="text-sm text-gray-900" data-id="element-9609">Maybank</span>
                          <span className="text-sm text-gray-500 font-mono" data-id="element-9610">
                            ••••5678
                          </span>
                        </div>
                      </div>
                      {pendingPayoutUpdate && <div className="ml-11 flex items-center gap-2 flex-wrap" data-id="element-9611">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200" data-id="element-9612">
                            <ClockIcon size={12} data-id="element-9613" />
                            Pending Bank Review
                          </span>
                          <span className="text-xs text-gray-600" data-id="element-9614">
                            New: {pendingPayoutUpdate.bank}{' '}
                            <span className="font-mono" data-id="element-9615">
                              {pendingPayoutUpdate.maskedAccount}
                            </span>
                          </span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9616">
                    <span className="text-sm text-gray-600" data-id="element-9617">Free</span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-9618">
                    <div className="flex items-center justify-end gap-2" data-id="element-9619">
                      <button onClick={() => navigate('/settings/payout-account/edit')} disabled={!!pendingPayoutUpdate} title={pendingPayoutUpdate ? 'An update is pending bank review' : 'Edit payout account'} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9620">
                        Edit
                      </button>
                      <button aria-label="More options" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-9621">
                        <MoreHorizontalIcon size={18} data-id="element-9622" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-9623">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-9624">
              + Add a settlement currency
            </button>
          </div>
        </div>

        {/* Minimum Balance Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9625">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9626">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9627">
              <DollarSignIcon size={18} className="text-gray-600" data-id="element-9628" />
              Minimum Balance
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-9629">
              Keep a minimum amount in your account to cover refunds and
              chargebacks
            </p>
          </div>

          <div className="p-6" data-id="element-9630">
            <div className="flex items-start gap-4" data-id="element-9631">
              <label className="relative inline-flex items-center cursor-pointer mt-0.5" data-id="element-9632">
                <input type="checkbox" className="sr-only peer" checked={minBalanceEnabled} onChange={e => setMinBalanceEnabled(e.target.checked)} data-id="element-9633" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9634"></div>
              </label>

              <div className="flex-1" data-id="element-9635">
                <p className="text-sm font-medium text-gray-900" data-id="element-9636">
                  Keep a minimum amount in your account
                </p>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-9637">
                  We'll adjust your payouts so you always maintain this balance
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-9638">
                    <div className="relative max-w-xs" data-id="element-9639">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9640">
                        <span className="text-gray-500 text-sm" data-id="element-9641">MYR</span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-14 pr-3 py-2 text-sm rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent ${isMinBalanceInvalid ? 'border-red-300 text-red-900' : 'border-gray-300'}`} placeholder="0.00" data-id="element-9642" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-9643">
                        <InfoIcon size={14} data-id="element-9644" />
                        Amount must be greater than 0
                      </p>}
                  </div>}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-9645">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-9646">
              Cancel
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isMinBalanceInvalid ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#FFC70A] hover:bg-[#E6B309] text-gray-900'}`} data-id="element-9647">
              Save
            </button>
          </div>
        </div>

        {/* Statement Descriptor Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9648">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9649">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9650">
              <FileTextIcon size={18} className="text-gray-600" data-id="element-9651" />
              Payout Statement Descriptor
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-9652">
              Customize how payouts appear on your bank statement
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-9653">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-9654">
              <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9655" />
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-9656">
                Set a custom statement descriptor to help identify payouts in
                your bank account. This will appear on your bank statement for
                all payouts.
              </p>
            </div>

            <FormField label="Statement Descriptor" description="This text will appear on your bank statement" data-id="element-9657">
              <input type="text" value={payoutDescriptor} onChange={e => setPayoutDescriptor(e.target.value)} placeholder={`Example - ${companyInfo.businessName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent text-sm" data-id="element-9658" />
            </FormField>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-9659">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-9660">
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-medium bg-[#FFC70A] hover:bg-[#E6B309] text-gray-900 rounded-md transition-colors" data-id="element-9661">
              Save
            </button>
          </div>
        </div>
      </div>;
  };
  // MDR row component
  const MdrRow = ({
    type,
    tier,
    range,
    ic,
    sf,
    am,
    total,
    color
  }: any) => <tr className={color} data-id="element-9662">
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-9663">{type}</td>
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-9664">{tier}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-9665">{range}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-9666">{ic}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-9667">{sf}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-9668">{am}</td>
      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-9669">{total}</td>
    </tr>;
  const FixedFee = ({
    label,
    sublabel,
    amount
  }: {
    label: string;
    sublabel: string;
    amount: number | string;
  }) => <div className="flex items-center justify-between" data-id="element-9670">
      <div data-id="element-9671">
        <p className="text-sm text-gray-900 font-medium" data-id="element-9672">{label}</p>
        <p className="text-xs text-gray-500" data-id="element-9673">{sublabel}</p>
      </div>
      <span className="text-sm font-semibold text-gray-900" data-id="element-9674">
        {typeof amount === 'number' ? formatCurrencyAmount(amount, {
        currency
      }) : amount}
      </span>
    </div>;
  const renderSubscriptionPlanSettings = () => <div data-id="element-9675">
      <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-9676">
        Subscription Plan
      </h2>
      <div className="space-y-6" data-id="element-9677">
        {/* Supported Payment Methods */}
        <div className="border-b border-gray-200 pb-6" data-id="element-9678">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-9679">
            <CreditCardIcon size={16} className="mr-2 text-blue-600" data-id="element-9680" />
            Supported Payment Methods
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9681">
            Payment methods enabled for your account with their merchant
            discount rates
          </p>

          {/* Card Payments */}
          <div className="mb-6" data-id="element-9682">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-9683">
              Card Payments
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-9684">
              {/* Visa */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9685">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-9686">
                  <div className="flex items-center justify-between" data-id="element-9687">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9688">
                      Visa
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9689">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-9690">
                  <div className="flex items-center justify-between" data-id="element-9691">
                    <span className="text-xs text-gray-600" data-id="element-9692">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9693">
                      0.80% - 1.30%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-9694">
                    <span className="text-xs text-gray-600" data-id="element-9695">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9696">
                      1.10% - 1.70%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-9697">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-9698">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-9699">
                      <p className="text-xs text-gray-600" data-id="element-9700">
                        <span className="font-medium" data-id="element-9701">MYR 1-1,000:</span> 0.80%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-9702">
                        <span className="font-medium" data-id="element-9703">MYR 1,000+:</span> 1.30%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mastercard */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9704">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200" data-id="element-9705">
                  <div className="flex items-center justify-between" data-id="element-9706">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9707">
                      Mastercard
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9708">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-9709">
                  <div className="flex items-center justify-between" data-id="element-9710">
                    <span className="text-xs text-gray-600" data-id="element-9711">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9712">
                      0.85% - 1.35%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-9713">
                    <span className="text-xs text-gray-600" data-id="element-9714">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9715">
                      1.15% - 1.75%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-9716">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-9717">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-9718">
                      <p className="text-xs text-gray-600" data-id="element-9719">
                        <span className="font-medium" data-id="element-9720">MYR 1-500:</span> 0.85%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-9721">
                        <span className="font-medium" data-id="element-9722">MYR 500+:</span> 1.35%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* American Express */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9723">
                <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200" data-id="element-9724">
                  <div className="flex items-center justify-between" data-id="element-9725">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9726">
                      American Express
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9727">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-9728">
                  <div className="flex items-center justify-between" data-id="element-9729">
                    <span className="text-xs text-gray-600" data-id="element-9730">All Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9731">
                      2.40% - 2.60%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-9732">
                    <p className="text-xs text-gray-500" data-id="element-9733">
                      + MYR 0.75 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* E-Wallets & Online Banking */}
          <div data-id="element-9734">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-9735">
              E-Wallets & Online Banking
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-9736">
              {/* Touch 'n Go */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9737">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-9738">
                  <div className="flex items-center justify-between" data-id="element-9739">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9740">
                      Touch 'n Go
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9741">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-9742">
                  <div className="flex items-center justify-between mb-2" data-id="element-9743">
                    <span className="text-xs text-gray-600" data-id="element-9744">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9745">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-9746">
                    <p className="text-xs text-gray-500" data-id="element-9747">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* GrabPay */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9748">
                <div className="bg-green-50 px-4 py-3 border-b border-green-200" data-id="element-9749">
                  <div className="flex items-center justify-between" data-id="element-9750">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9751">
                      GrabPay
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9752">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-9753">
                  <div className="flex items-center justify-between mb-2" data-id="element-9754">
                    <span className="text-xs text-gray-600" data-id="element-9755">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9756">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-9757">
                    <p className="text-xs text-gray-500" data-id="element-9758">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* FPX */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9759">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200" data-id="element-9760">
                  <div className="flex items-center justify-between" data-id="element-9761">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9762">
                      FPX
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9763">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-9764">
                  <div className="flex items-center justify-between mb-2" data-id="element-9765">
                    <span className="text-xs text-gray-600" data-id="element-9766">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9767">
                      1.20%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-9768">
                    <p className="text-xs text-gray-500" data-id="element-9769">
                      + MYR 0.40 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* DuitNow QR */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9770">
                <div className="bg-amber-50 px-4 py-3 border-b border-amber-200" data-id="element-9771">
                  <div className="flex items-center justify-between" data-id="element-9772">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9773">
                      DuitNow QR
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9774">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-9775">
                  <div className="flex items-center justify-between mb-2" data-id="element-9776">
                    <span className="text-xs text-gray-600" data-id="element-9777">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-9778">
                      0.80%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-9779">
                    <p className="text-xs text-gray-500" data-id="element-9780">
                      + MYR 0.25 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9781">
            <div className="flex items-start gap-2" data-id="element-9782">
              <InfoIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9783" />
              <div className="text-sm text-gray-700" data-id="element-9784">
                <p className="font-medium text-gray-900 mb-1" data-id="element-9785">Pricing Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-9786">
                  <li data-id="element-9787">
                    Card rates vary based on card type (debit/credit) and
                    issuing bank
                  </li>
                  <li data-id="element-9788">
                    Lower rates apply to Maybank-issued cards (on-us
                    transactions)
                  </li>
                  <li data-id="element-9789">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Fees & Additional Charges */}
        <div className="border-b border-gray-200 pb-6" data-id="element-9790">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-9791">
            <TagIcon size={16} className="mr-2 text-blue-600" data-id="element-9792" />
            Transaction Fees & Additional Charges
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9793">
            Fixed fees and additional charges per transaction or event
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-9794">
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9795">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-9796">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-9797">
                  Card Transaction Fees
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-9798">
                <FixedFee label="Visa/Mastercard" sublabel="Per successful transaction" amount={0.5} data-id="element-9799" />
                <FixedFee label="American Express" sublabel="Per successful transaction" amount={0.75} data-id="element-9800" />
                <FixedFee label="Authorization Fee" sublabel="Per authorization attempt" amount={0.2} data-id="element-9801" />
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9802">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-9803">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-9804">
                  E-Wallet & Online Banking
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-9805">
                <FixedFee label="Touch 'n Go / GrabPay" sublabel="Per successful transaction" amount={0.3} data-id="element-9806" />
                <FixedFee label="FPX (Online Banking)" sublabel="Per successful transaction" amount={0.4} data-id="element-9807" />
                <FixedFee label="DuitNow QR" sublabel="Per successful transaction" amount={0.25} data-id="element-9808" />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9809">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-9810">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-9811">
                Additional Service Fees
              </h4>
            </div>
            <table className="w-full text-left" data-id="element-9812">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9813">
                <tr data-id="element-9814">
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-9815">
                    Service
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-9816">
                    Description
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-9817">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9818">
                <tr data-id="element-9819">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9820">
                    Refund Processing
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-9821">
                    Per refund transaction
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-9822">
                    {formatCurrencyAmount(1.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-9823">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9824">
                    Chargeback Fee
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-9825">
                    Per chargeback filed
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-9826">
                    {formatCurrencyAmount(50.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-9827">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9828">
                    Chargeback Reversal
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-9829">
                    When chargeback is won
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right" data-id="element-9830">{`-${formatCurrencyAmount(50.0, {
                    currency
                  })}`}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-9831">
            <div className="flex items-start gap-2" data-id="element-9832">
              <InfoIcon size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9833" />
              <div className="text-sm text-gray-700" data-id="element-9834">
                <p className="font-medium text-gray-900 mb-1" data-id="element-9835">Fee Structure:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-9836">
                  <li data-id="element-9837">
                    <span className="font-medium" data-id="element-9838">Total cost</span> =
                    (Transaction Amount × MDR%) + Fixed Fee
                  </li>
                  <li data-id="element-9839">
                    Example: MYR 100 Visa on-us = (MYR 100 × 1.10%) + MYR 0.50 =
                    MYR 1.60
                  </li>
                  <li data-id="element-9840">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Fees */}
        <div className="border-b border-gray-200 pb-6" data-id="element-9841">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-9842">
            <DollarSignIcon size={16} className="mr-2 text-blue-600" data-id="element-9843" />
            Subscription Fees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9844">
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-9845">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-9846">
                Annual Rental
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-9847">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-9848">Billed annually</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-9849">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-9850">
                Monthly Equivalent
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-9851">
                {formatCurrencyAmount(200, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-9852">
                MYR 2,400 / 12 months
              </p>
            </div>
          </div>
        </div>

        {/* Last Payment */}
        <div data-id="element-9853">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-9854">
            <ClockIcon size={16} className="mr-2 text-blue-600" data-id="element-9855" />
            Last Subscription Payment
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-9856">
            <div data-id="element-9857">
              <p className="text-sm font-medium text-gray-900" data-id="element-9858">
                Annual Subscription Renewal
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-9859">
                Payment Date: January 15, 2026
              </p>
            </div>
            <div className="text-right" data-id="element-9860">
              <p className="text-lg font-bold text-gray-900" data-id="element-9861">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-9862">
                Paid
              </span>
            </div>
          </div>
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-9863">
            <p className="text-xs text-gray-600" data-id="element-9864">
              <span className="font-medium" data-id="element-9865">Next renewal:</span> January 15,
              2027
            </p>
          </div>
        </div>
      </div>
    </div>;
  const renderShopSettings = () => <div className="space-y-6" data-id="element-9866">
      <div className="flex items-center justify-between" data-id="element-9867">
        <div data-id="element-9868">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9869">
            Shop Settings
          </h2>
          <p className="text-sm text-gray-600" data-id="element-9870">
            Configure inventory, pricing, and shop-specific preferences
          </p>
        </div>
        {/* Shop Selector */}
        <div className="relative" data-id="element-9871">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-9872">
            <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center" data-id="element-9873">
              <ShoppingBagIcon size={16} className="text-pink-600" data-id="element-9874" />
            </div>
            <span className="text-sm font-medium text-gray-900" data-id="element-9875">
              {selectedShop}
            </span>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9876">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-9877" />
            </svg>
          </button>
        </div>
      </div>

      {/* Shop-specific Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-9878">
        <div className="flex items-start gap-3" data-id="element-9879">
          <InfoIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9880" />
          <div className="text-sm text-gray-700" data-id="element-9881">
            <p data-id="element-9882">
              These settings apply specifically to{' '}
              <span className="font-semibold text-amber-900" data-id="element-9883">
                {selectedShop}
              </span>
              . Switch shops using the selector above to configure settings for
              other shops.
            </p>
          </div>
        </div>
      </div>

      {/* Inventory Management Section */}
      <div data-id="element-9884">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9885">
          <PackageIcon size={20} className="text-blue-600" data-id="element-9886" />
          Inventory Management
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-9887">
          <div className="flex items-start gap-3" data-id="element-9888">
            <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9889" />
            <div className="text-sm text-gray-700" data-id="element-9890">
              <p data-id="element-9891">
                Configure how inventory is managed for {selectedShop}. These
                settings affect stock tracking and product availability.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4" data-id="element-9892">
          <div className="flex items-center justify-between" data-id="element-9893">
            <div data-id="element-9894">
              <p className="text-sm font-medium text-gray-900" data-id="element-9895">
                Enable Stock Management
              </p>
              <p className="text-xs text-gray-500" data-id="element-9896">
                Track inventory levels for products in this shop
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9897">
              <input type="checkbox" className="sr-only peer" checked={shopSettings.inventoryTracking} onChange={e => setShopSettings({
              ...shopSettings,
              inventoryTracking: e.target.checked
            })} data-id="element-9898" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9899"></div>
            </label>
          </div>

          <div data-id="element-9900">
            <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-9901">
              Low Stock Threshold
            </label>
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={shopSettings.lowStockThreshold} onChange={e => setShopSettings({
            ...shopSettings,
            lowStockThreshold: parseInt(e.target.value)
          })} data-id="element-9902" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-9903">
              Products with stock below this number will be marked as low stock
            </p>
          </div>

          <div className="flex items-center justify-between" data-id="element-9904">
            <div data-id="element-9905">
              <p className="text-sm font-medium text-gray-900" data-id="element-9906">
                Notify on Low Stock
              </p>
              <p className="text-xs text-gray-500" data-id="element-9907">
                Receive notifications when products reach low stock threshold
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9908">
              <input type="checkbox" className="sr-only peer" checked={shopSettings.notifyOnLowStock} onChange={e => setShopSettings({
              ...shopSettings,
              notifyOnLowStock: e.target.checked
            })} data-id="element-9909" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9910"></div>
            </label>
          </div>

          <div className="flex items-center justify-between" data-id="element-9911">
            <div data-id="element-9912">
              <p className="text-sm font-medium text-gray-900" data-id="element-9913">
                Allow Backorders
              </p>
              <p className="text-xs text-gray-500" data-id="element-9914">
                Allow customers to purchase products that are out of stock
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9915">
              <input type="checkbox" className="sr-only peer" checked={shopSettings.allowBackorders} onChange={e => setShopSettings({
              ...shopSettings,
              allowBackorders: e.target.checked
            })} data-id="element-9916" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9917"></div>
            </label>
          </div>

          <div className="flex items-center justify-between" data-id="element-9918">
            <div data-id="element-9919">
              <p className="text-sm font-medium text-gray-900" data-id="element-9920">
                Hide Out of Stock Products
              </p>
              <p className="text-xs text-gray-500" data-id="element-9921">
                Automatically hide products when they are out of stock
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9922">
              <input type="checkbox" className="sr-only peer" checked={shopSettings.hideOutOfStock} onChange={e => setShopSettings({
              ...shopSettings,
              hideOutOfStock: e.target.checked
            })} data-id="element-9923" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9924"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Tax Configuration Section */}
      <div data-id="element-9925">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9926">
          <PercentIcon size={20} className="text-blue-600" data-id="element-9927" />
          Tax Configuration
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-9928">
          <div className="flex items-start gap-3" data-id="element-9929">
            <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9930" />
            <div className="text-sm text-gray-700" data-id="element-9931">
              <p data-id="element-9932">
                Configure tax settings for {selectedShop}. These settings
                determine how taxes are calculated and displayed.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 mb-6" data-id="element-9933">
          <div className="flex items-center justify-between" data-id="element-9934">
            <div data-id="element-9935">
              <p className="text-sm font-medium text-gray-900" data-id="element-9936">
                Enable Tax Calculation
              </p>
              <p className="text-xs text-gray-500" data-id="element-9937">
                Calculate and apply taxes to orders
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9938">
              <input type="checkbox" className="sr-only peer" checked={shopSettings.enableTaxCalculation} onChange={e => setShopSettings({
              ...shopSettings,
              enableTaxCalculation: e.target.checked
            })} data-id="element-9939" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9940"></div>
            </label>
          </div>

          <div className="flex items-center justify-between" data-id="element-9941">
            <div data-id="element-9942">
              <p className="text-sm font-medium text-gray-900" data-id="element-9943">
                Prices Include Tax
              </p>
              <p className="text-xs text-gray-500" data-id="element-9944">
                Product prices already include tax
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9945">
              <input type="checkbox" className="sr-only peer" checked={shopSettings.pricesIncludeTax} onChange={e => setShopSettings({
              ...shopSettings,
              pricesIncludeTax: e.target.checked
            })} data-id="element-9946" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9947"></div>
            </label>
          </div>
        </div>

        {/* Tax Rates Table */}
        <div data-id="element-9948">
          <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-9949">
            Tax Rates
          </h4>
          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9950">
            <table className="w-full" data-id="element-9951">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9952">
                <tr data-id="element-9953">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-9954">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-9955">
                    Rate (%)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-9956">
                    Default
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-9957">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9958">
                {taxRates.map(rate => <tr key={rate.id} className="hover:bg-gray-50" data-id="element-9959">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9960">
                      {rate.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-id="element-9961">
                      {rate.rate}%
                    </td>
                    <td className="px-4 py-3" data-id="element-9962">
                      {rate.isDefault ? <span className="text-green-600 flex items-center gap-1" data-id="element-9963">
                          <CheckCircleIcon size={16} data-id="element-9964" />
                        </span> : <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-9965">
                          Set as default
                        </button>}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-9966">
                      <button className="text-sm text-blue-600 hover:text-blue-700 mr-3" data-id="element-9967">
                        Edit
                      </button>
                      <button className="text-sm text-red-600 hover:text-red-700" data-id="element-9968">
                        Delete
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <button className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-9969">
            <PlusIcon size={16} data-id="element-9970" />
            Add Tax Rate
          </button>
        </div>
      </div>

      {/* Customer Information Fields Section */}
      <div data-id="element-9971">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9972">
          <FormInputIcon size={20} className="text-blue-600" data-id="element-9973" />
          Customer Information Fields
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-9974">
          <div className="flex items-start gap-3" data-id="element-9975">
            <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9976" />
            <div className="text-sm text-gray-700" data-id="element-9977">
              <p data-id="element-9978">
                Configure the information fields that customers need to fill
                when placing an order in {selectedShop}.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9979">
          <table className="w-full" data-id="element-9980">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9981">
              <tr data-id="element-9982">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-9983">
                  Field Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-9984">
                  Type
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-9985">
                  Required
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-9986">
                  Enabled
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-9987">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9988">
              {customerFields.map(field => <tr key={field.id} className="hover:bg-gray-50" data-id="element-9989">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9990">
                    {field.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-9991">
                    {field.type}
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-9992">
                    <input type="checkbox" checked={field.required} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" readOnly data-id="element-9993" />
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-9994">
                    <input type="checkbox" checked={field.enabled} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" readOnly data-id="element-9995" />
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-9996">
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-9997">
                      Edit
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <button className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-9998">
          <PlusIcon size={16} data-id="element-9999" />
          Add Custom Field
        </button>
      </div>

      {/* Custom Fees & Service Charges Section */}
      <div data-id="element-10000">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-10001">
          <DollarSignIcon size={20} className="text-blue-600" data-id="element-10002" />
          Custom Fees & Service Charges
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-10003">
          <div className="flex items-start gap-3" data-id="element-10004">
            <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10005" />
            <div className="text-sm text-gray-700" data-id="element-10006">
              <p data-id="element-10007">
                Configure additional fees and service charges for {selectedShop}
                . These will be added to customer orders based on your rules.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden mb-4" data-id="element-10008">
          <table className="w-full" data-id="element-10009">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10010">
              <tr data-id="element-10011">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-10012">
                  Fee Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-10013">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-10014">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-10015">
                  Conditions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10016">
              {shopFees.map(fee => <tr key={fee.id} className="hover:bg-gray-50" data-id="element-10017">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10018">
                    {fee.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900" data-id="element-10019">
                    {fee.type === 'Percentage' ? `${fee.amount}%` : `MYR ${fee.amount.toFixed(2)}`}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10020">
                    {fee.type}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500" data-id="element-10021">
                    {fee.condition}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-10022">
          <PlusIcon size={16} data-id="element-10023" />
          Add Fee
        </button>
      </div>

      {/* Payment & Payout Settings Section */}
      <div data-id="element-10024">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-10025">
          <WalletIcon size={20} className="text-blue-600" data-id="element-10026" />
          Payment & Payout Settings
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-10027">
          <div className="flex items-start gap-3" data-id="element-10028">
            <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10029" />
            <div className="text-sm text-gray-700" data-id="element-10030">
              <p data-id="element-10031">
                Configure payment and payout settings for {selectedShop}. These
                settings determine how you receive payments from sales.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4" data-id="element-10032">
          <FormField label="Payout Bank Account" description="Enter the bank account where you want to receive your payouts" data-id="element-10033">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={shopPayoutSettings.bankAccount} onChange={e => setShopPayoutSettings({
            ...shopPayoutSettings,
            bankAccount: e.target.value
          })} data-id="element-10034" />
          </FormField>

          <FormField label="Payout Frequency" description="How often you want to receive payouts" data-id="element-10035">
            <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={shopPayoutSettings.frequency} onChange={e => setShopPayoutSettings({
            ...shopPayoutSettings,
            frequency: e.target.value
          })} data-id="element-10036">
              <option data-id="element-10037">Daily</option>
              <option data-id="element-10038">Weekly</option>
              <option data-id="element-10039">Monthly</option>
            </select>
          </FormField>

          <FormField label="Minimum Payout Amount" description="Payouts will only be processed when the balance exceeds this amount" data-id="element-10040">
            <div className="relative" data-id="element-10041">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10042">
                <span className="text-gray-500 text-sm" data-id="element-10043">MYR</span>
              </div>
              <input type="number" className="block w-full pl-14 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={shopPayoutSettings.minimumAmount} onChange={e => setShopPayoutSettings({
              ...shopPayoutSettings,
              minimumAmount: parseFloat(e.target.value)
            })} data-id="element-10044" />
            </div>
          </FormField>
        </div>
      </div>

      {/* Order Notifications Section */}
      <div data-id="element-10045">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-10046">
          <BellIcon size={20} className="text-blue-600" data-id="element-10047" />
          Order Notifications
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-10048">
          <div className="flex items-start gap-3" data-id="element-10049">
            <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10050" />
            <div className="text-sm text-gray-700" data-id="element-10051">
              <p data-id="element-10052">
                Configure order notifications and confirmation messages for{' '}
                {selectedShop}.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4" data-id="element-10053">
          <FormField label="Order Confirmation Message" description="This message will be displayed to customers after they complete an order" data-id="element-10054">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" rows={3} value={shopNotifications.confirmationMessage} onChange={e => setShopNotifications({
            ...shopNotifications,
            confirmationMessage: e.target.value
          })} data-id="element-10055" />
          </FormField>

          <FormField label="Notification Email" description="Email address where order notifications will be sent" data-id="element-10056">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={shopNotifications.notificationEmail} onChange={e => setShopNotifications({
            ...shopNotifications,
            notificationEmail: e.target.value
          })} data-id="element-10057" />
          </FormField>

          <FormField label="Notification Phone Number" description="Phone number where order notifications will be sent" data-id="element-10058">
            <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={shopNotifications.notificationPhone} onChange={e => setShopNotifications({
            ...shopNotifications,
            notificationPhone: e.target.value
          })} data-id="element-10059" />
          </FormField>

          <div className="space-y-3 pt-2" data-id="element-10060">
            <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-10061">
              <div data-id="element-10062">
                <p className="text-sm font-medium text-gray-900" data-id="element-10063">
                  Send Order Notifications
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-10064">
                  Receive notifications for new orders
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-id="element-10065">
                <input type="checkbox" className="sr-only peer" checked={shopNotifications.sendOrderNotifications} onChange={e => setShopNotifications({
                ...shopNotifications,
                sendOrderNotifications: e.target.checked
              })} data-id="element-10066" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10067"></div>
              </label>
            </div>

            <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3" data-id="element-10068">
              <div data-id="element-10069">
                <p className="text-sm font-medium text-gray-900" data-id="element-10070">
                  Send Low Stock Alerts
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-10071">
                  Receive notifications when products reach low stock levels
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer" data-id="element-10072">
                <input type="checkbox" className="sr-only peer" checked={shopNotifications.sendLowStockAlerts} onChange={e => setShopNotifications({
                ...shopNotifications,
                sendLowStockAlerts: e.target.checked
              })} data-id="element-10073" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#FFC70A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10074"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-10075" />
    </div>;
  const renderOrderSettings = () => <div className="space-y-6" data-id="element-10076">
      <div data-id="element-10077">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10078">
          Order Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10079">
          Configure order numbering, invoicing, and payment terms
        </p>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10080">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10081">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10082">
            <PackageIcon size={18} className="text-gray-600" data-id="element-10083" />
            Order Numbering
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10084">
          <div className="grid grid-cols-2 gap-4" data-id="element-10085">
            <FormField label="Order Prefix" description="Prefix for order numbers" data-id="element-10086">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={orderSettings.orderPrefix} onChange={e => setOrderSettings({
              ...orderSettings,
              orderPrefix: e.target.value
            })} data-id="element-10087" />
            </FormField>
            <FormField label="Starting Number" description="First order number" data-id="element-10088">
              <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent" value={orderSettings.startingNumber} onChange={e => setOrderSettings({
              ...orderSettings,
              startingNumber: parseInt(e.target.value)
            })} data-id="element-10089" />
            </FormField>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-10090" />
    </div>;
  const renderActivityLogs = () => {
    const filteredLogs = activityLogs.filter(log => {
      if (logFilters.search && !log.description.toLowerCase().includes(logFilters.search.toLowerCase()) && !log.user.name.toLowerCase().includes(logFilters.search.toLowerCase())) return false;
      if (logFilters.user !== 'all' && log.user.id !== logFilters.user) return false;
      if (logFilters.section !== 'all' && log.section !== logFilters.section) return false;
      if (logFilters.dateRange !== 'all') {
        const logDate = new Date(log.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));
        if (logFilters.dateRange === '24h' && daysDiff > 1) return false;
        if (logFilters.dateRange === '7days' && daysDiff > 7) return false;
        if (logFilters.dateRange === '30days' && daysDiff > 30) return false;
      }
      return true;
    });
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
    const paginatedLogs = filteredLogs.slice((currentLogPage - 1) * logsPerPage, currentLogPage * logsPerPage);
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10091">
        <div data-id="element-10092">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10093">
            Activity Logs
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10094">
            View and track all user activities and system events
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10095">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10096">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10097">
              <SearchIcon size={18} className="text-gray-600" data-id="element-10098" />
              Filters
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-10099">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10100">
              <div data-id="element-10101">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10102">
                  Search
                </label>
                <input type="text" placeholder="Search logs..." className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent text-sm" value={logFilters.search} onChange={e => setLogFilters({
                ...logFilters,
                search: e.target.value
              })} data-id="element-10103" />
              </div>
              <div data-id="element-10104">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10105">
                  Date Range
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent text-sm" value={logFilters.dateRange} onChange={e => setLogFilters({
                ...logFilters,
                dateRange: e.target.value
              })} data-id="element-10106">
                  <option value="all" data-id="element-10107">All Time</option>
                  <option value="24h" data-id="element-10108">Last 24 Hours</option>
                  <option value="7days" data-id="element-10109">Last 7 Days</option>
                  <option value="30days" data-id="element-10110">Last 30 Days</option>
                </select>
              </div>
              <div data-id="element-10111">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10112">
                  User
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent text-sm" value={logFilters.user} onChange={e => setLogFilters({
                ...logFilters,
                user: e.target.value
              })} data-id="element-10113">
                  <option value="all" data-id="element-10114">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id} data-id="element-10115">
                      {u.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-10116">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10117">
                  Section
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent text-sm" value={logFilters.section} onChange={e => setLogFilters({
                ...logFilters,
                section: e.target.value
              })} data-id="element-10118">
                  <option value="all" data-id="element-10119">All Sections</option>
                  <option value="Payments" data-id="element-10120">Payments</option>
                  <option value="Customers" data-id="element-10121">Customers</option>
                  <option value="Invoices" data-id="element-10122">Invoices</option>
                  <option value="Bills" data-id="element-10123">Bills</option>
                  <option value="Refunds" data-id="element-10124">Refunds</option>
                  <option value="Users" data-id="element-10125">Users</option>
                  <option value="Settings" data-id="element-10126">Settings</option>
                  <option value="Security" data-id="element-10127">Security</option>
                </select>
              </div>
              <div data-id="element-10128">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10129">
                  Per Page
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FFC70A] focus:border-transparent text-sm" value={logsPerPage} onChange={e => {
                setLogsPerPage(Number(e.target.value));
                setCurrentLogPage(1);
              }} data-id="element-10130">
                  <option value="25" data-id="element-10131">25</option>
                  <option value="50" data-id="element-10132">50</option>
                  <option value="100" data-id="element-10133">100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-10134">
              <p className="text-sm text-gray-600" data-id="element-10135">
                Showing {paginatedLogs.length} of {filteredLogs.length} logs
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10136">
          <div className="overflow-x-auto" data-id="element-10137">
            <table className="w-full" data-id="element-10138">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10139">
                <tr data-id="element-10140">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10141">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10142">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10143">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10144">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10145">
                    Object ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10146">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10147">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-id="element-10148">
                    <td className="px-6 py-4 text-sm text-gray-900" title={new Date(log.timestamp).toLocaleString()} data-id="element-10149">
                      {getRelativeTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10150">
                      <div className="flex items-center gap-2" data-id="element-10151">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold" data-id="element-10152">
                          {log.user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div data-id="element-10153">
                          <p className="text-sm font-medium text-gray-900" data-id="element-10154">
                            {log.user.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-10155">
                            {log.user.role}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-10156">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10157">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10158">
                      {log.section}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10159">
                      {log.objectId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10160">
                      {log.ipAddress}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-10161">
              <button onClick={() => setCurrentLogPage(Math.max(1, currentLogPage - 1))} disabled={currentLogPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10162">
                Previous
              </button>
              <span className="text-sm text-gray-600" data-id="element-10163">
                Page {currentLogPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentLogPage(Math.min(totalPages, currentLogPage + 1))} disabled={currentLogPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10164">
                Next
              </button>
            </div>}
        </div>
      </div>;
  };
  const renderRequests = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Closed':
          return 'bg-gray-100 text-gray-800';
        case 'Pending':
          return 'bg-amber-100 text-amber-800';
        case 'In Progress':
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10165">
        <div data-id="element-10166">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10167">
            Merchant Requests
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10168">
            View and track requests for payment method activations and custom
            user roles
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10169">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10170">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10171">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-10172" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-10173">
            <table className="w-full" data-id="element-10174">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10175">
                <tr data-id="element-10176">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10177">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10178">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10179">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10180">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10181">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10182">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10183">
                {merchantRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 transition-colors" data-id="element-10184">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-10185">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10186">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-10187">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10188">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-10189">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10190">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-10191">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10192">
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10193">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10194">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-10195">
              {merchantRequests.length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10196">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10197">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-10198">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10199">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10200">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-10201">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>
      </div>;
  };
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'company':
        return renderCompanySettings();
      case 'branding':
        return renderBrandingSettings();
      case 'general':
        return renderGeneralSettings();
      case 'receipt':
        return renderReceiptSettings();
      case 'payment-providers':
        return renderPaymentProviders();
      case 'users':
        return renderUsers();
      case 'payout':
        return renderPayoutSettings();
      case 'fields':
        return renderFields();
      case 'notifications':
        return renderNotifications();
      case 'security':
        return renderSecurity();
      case 'subscription-plan':
        return renderSubscriptionPlanSettings();
      case 'shop-settings':
        return renderShopSettings();
      case 'order-settings':
        return renderOrderSettings();
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto py-4 sm:py-8 px-4" data-id="element-10202">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-10203">
        Settings
      </h1>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-10204">
        <nav className="lg:w-64 flex-shrink-0" data-id="element-10205">
          <div className="lg:hidden mb-4" data-id="element-10206">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-10207">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'bg-[#FFC70A] text-gray-900' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} data-id="element-10208">
                    <Icon size={16} data-id="element-10209" />
                    <span data-id="element-10210">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>
          <div className="hidden lg:block" data-id="element-10211">
            <div className="space-y-6" data-id="element-10212">
              {categories.map(category => <div key={category.id} data-id="element-10213">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-10214">
                    {category.label}
                  </h3>
                  <ul className="space-y-1" data-id="element-10215">
                    {tabs.filter(tab => tab.category === category.id).map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return <li key={tab.id} data-id="element-10216">
                            <button onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-[#FFC70A] text-gray-900' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-10217">
                              <Icon size={16} data-id="element-10218" />
                              <span data-id="element-10219">{tab.label}</span>
                            </button>
                          </li>;
                })}
                  </ul>
                </div>)}
            </div>
          </div>
        </nav>
        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 overflow-x-auto" data-id="element-10220">
          {renderActiveTab()}
        </div>
      </div>
    </div>;
}